// @ts-nocheck
import { Eye, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useApi } from '../../hooks/useApi'

export default function BusinessHoursPreview({ phoneNumberId, businessHours }) {
  const api = useApi()
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // 営業時間ステータスを取得
  const fetchStatus = async () => {
    if (!businessHours.enabled) {
      setStatus(null)
      return
    }

    try {
      setLoading(true)
      setError(null)

      let endpoint = `/api/phone-numbers/${phoneNumberId}/business-hours/status`
      if (businessHours?.timezone) {
        endpoint += `?timezone=${encodeURIComponent(businessHours.timezone)}`
      }

      const data = await api.get(endpoint)
      setStatus(data)
    } catch (err) {
      console.error('ステータス取得エラー:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // 初回読み込みと定期更新
  useEffect(() => {
    fetchStatus()
    const interval = setInterval(fetchStatus, 60000) // 1分ごとに更新
    return () => clearInterval(interval)
  }, [phoneNumberId, businessHours, api])

  if (!businessHours.enabled) {
    return null
  }

  if (loading && !status) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!status) {
    return null
  }

  const displayTime = new Date(status.currentTime).toLocaleString('ja-JP', {
    timeZone: status.timezone || 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
            <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-md font-semibold text-gray-900 dark:text-white">
              リアルタイムプレビュー
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">現在の営業状態を確認</p>
          </div>
        </div>
      </div>
      <div className="p-6">
        {/* 現在の営業状態 */}
        <div className="space-y-4">
          <div
            className={`p-4 rounded-lg border-2 ${
              status.isOpen
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              {status.isOpen ? (
                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              )}
              <div className="flex-1">
                <h4
                  className={`text-lg font-semibold ${
                    status.isOpen
                      ? 'text-green-900 dark:text-green-100'
                      : 'text-red-900 dark:text-red-100'
                  }`}
                >
                  {status.isOpen ? '営業中' : '営業時間外'}
                </h4>
                <p
                  className={`text-sm ${
                    status.isOpen
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-red-700 dark:text-red-300'
                  }`}
                >
                  {displayTime} ({status.timezone || 'Asia/Tokyo'})
                </p>
              </div>
            </div>
            {status.reason && (
              <p
                className={`text-sm ${
                  status.isOpen
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-red-700 dark:text-red-300'
                }`}
              >
                {/* 簡潔なメッセージのみ表示 */}
                {status.reason === 'day_closed' && '本日は定休日です'}
                {status.reason === 'holiday_closed' && '祝日設定のため休業です'}
                {status.reason === 'holiday_outside_hours' && '祝日の営業時間外です'}
                {status.reason === 'holiday_open' && '祝日の営業時間内です'}
                {status.reason === 'schedule_not_configured' && '営業時間が設定されていません'}
                {![ 'day_closed', 'holiday_closed', 'holiday_outside_hours', 'holiday_open', 'schedule_not_configured' ].includes(status.reason) && ''}
              </p>
            )}
          </div>

          {/* 次回営業時間 */}
          {status.nextBusinessHours && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    次回営業時間
                  </h5>
                  {status.nextBusinessHours.date && (
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {status.nextBusinessHours.date} {status.nextBusinessHours.time || ''}
                    </p>
                  )}
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {status.nextBusinessHours.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 本日の営業時間 */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              本日の営業時間
            </h5>
            {status.today?.isOpen && status.today?.timeSlots?.length > 0 ? (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {status.today.timeSlots.map((slot) => `${slot.start} - ${slot.end}`).join(', ')}
              </p>
            ) : (
              <p className="text-sm text-gray-700 dark:text-gray-300">本日休業</p>
            )}
          </div>

          {/* 着信時の動作 */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              現在の着信時の動作
            </h5>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {status.isOpen
                ? '✓ 着信は通常通りキューに登録され、オペレーターに振り分けられます'
                : '✓ 着信は営業時間外メッセージで自動応答されます'}
            </p>
            {!status.isOpen && (
              <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                <p>メッセージ: {businessHours.closedMessage.ttsText.substring(0, 50)}...</p>
                <p className="mt-1">
                  アクション:{' '}
                  {businessHours.closedAction.action === 'hangup'
                    ? '電話を切る'
                    : businessHours.closedAction.action === 'voicemail'
                    ? 'ボイスメール録音'
                    : '転送'}
                </p>
              </div>
            )}
          </div>

          {/* 更新時刻 */}
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
            最終更新: {new Date().toLocaleTimeString('ja-JP')}
          </div>
        </div>
      </div>
    </div>
  )
}
